<template>
    <div class="category-roster-preview">
        <!-- Always a box of the default SelectionList height, so it lines up with the category list beside it. -->
        <div
            v-if="parameterId === null || (loaded && !loading && competitors.length === 0)"
            class="roster-placeholder selection-list selection-list-container is-flex is-align-items-center is-justify-content-center has-text-grey-light has-text-centered p-4"
        >
            {{
                translate(parameterId === null ? "admin_competitors.roster_preview_placeholder" : "admin_competitors.roster_preview_empty")
            }}
        </div>
        <template v-else>
            <SelectionList :options="competitors" :is-loading="loading" readonly>
                <template #option="{ option, index }">
                    <div class="roster-row is-flex is-align-items-center">
                        <div class="is-flex-grow-1">
                            <div class="is-flex is-align-items-center">
                                <span class="has-text-grey-light mr-2">#{{ index + 1 }}</span>
                                <Country class="mr-1" :country-code="option.nationality" :only-flag="true" />
                                <span class="clarity-mask has-text-white">{{ option.firstName }} {{ option.lastName }}</span>
                            </div>
                            <div class="has-text-grey-light is-size-7">
                                <span>{{ option.academy }}</span>
                                <span class="clarity-mask ml-2">{{ birth(option) }}</span>
                            </div>
                        </div>
                        <RegisteredCompetitorStatusTag class="ml-2 is-flex-shrink-0" :status="option.status.value" />
                    </div>
                </template>
            </SelectionList>
            <p v-if="hasNextPage" class="help mt-2">{{ translate("admin_competitors.roster_preview_overflow", false, [ROSTER_LIMIT]) }}</p>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { ref, watch } from "vue";
    import Country from "../Common/Country.vue";
    import SelectionList from "../Common/SelectionList.vue";
    import RegisteredCompetitorStatusTag from "./RegisteredCompetitorStatusTag.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { formatCalendarDate } from "../Common/dateTimeUtils";
    import paymentsApi, { CompetitorPaymentWithMetadata } from "../Api/payments";

    const ROSTER_LIMIT = 100;

    const props = defineProps<{
        eventId: number;
        parameterId: number | null;
    }>();

    const loading = ref(false);
    const loaded = ref(false);
    const hasNextPage = ref(false);
    const competitors = ref<CompetitorPaymentWithMetadata[]>([]);

    function birth(competitor: CompetitorPaymentWithMetadata): string {
        if (competitor.birthDate) {
            return formatCalendarDate(competitor.birthDate);
        }
        return competitor.birthYear ? String(competitor.birthYear) : "";
    }

    function load(parameterId: number | null) {
        if (parameterId === null) {
            loaded.value = false;
            hasNextPage.value = false;
            competitors.value = [];
            loading.value = false;
            return;
        }

        // The previous roster stays visible under the loading overlay and is replaced when the new one arrives,
        // so switching categories does not make the list collapse and jump.

        loading.value = true;
        paymentsApi
            .getCompetitorPaymentsPage(props.eventId, { parameterId, limit: ROSTER_LIMIT, sortField: "lastName", sortDirection: "asc" })
            .then(page => {
                if (props.parameterId !== parameterId) {
                    return;
                }
                competitors.value = page.result;
                hasNextPage.value = page.hasNextPage;
                loaded.value = true;
                loading.value = false;
            })
            .catch(err => {
                if (props.parameterId === parameterId) {
                    competitors.value = [];
                    hasNextPage.value = false;
                    loaded.value = false;
                    loading.value = false;
                    handleError(err);
                }
            });
    }

    watch(() => props.parameterId, load, { immediate: true });
</script>

<style scoped>
    .roster-placeholder {
        border-style: dashed;
    }
</style>
